import { createSlice } from "@reduxjs/toolkit";
import { PayrollResponse } from "../../shared/types";
import { PayrollThunks } from "../thunks";

interface State {
  payrolls: PayrollResponse[];
  collaboratorPayrolls: PayrollResponse[];
  periodPayrolls: PayrollResponse[];
  isLoading: boolean;
}

const initialState: State = {
  payrolls: [],
  collaboratorPayrolls: [],
  periodPayrolls: [],
  isLoading: true,
};

export const payrollSlice = createSlice({
  name: "payroll",
  initialState,
  reducers: {
    clearCollaboratorPayrolls: (state) => {
      state.collaboratorPayrolls = [];
    },
    clearPeriodPayrolls: (state) => {
      state.periodPayrolls = [];
    },
  },
  extraReducers: (builder) => {
    // Basic CRUD operations
    builder.addCase(PayrollThunks.fetchItems.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(PayrollThunks.fetchItems.fulfilled, (state, action) => {
      state.payrolls = action.payload;
      state.isLoading = false;
    });
    builder.addCase(PayrollThunks.fetchItems.rejected, (state) => {
      state.isLoading = false;
    });

    builder.addCase(PayrollThunks.createItem.fulfilled, (state, action) => {
      state.payrolls.push(action.payload);
    });

    builder.addCase(PayrollThunks.updateItem.fulfilled, (state, action) => {
      const index = state.payrolls.findIndex(
        (payroll) => payroll.id === action.payload.id
      );
      if (index !== -1) {
        state.payrolls[index] = action.payload;
      }

      // Update in collaborator payrolls if exists
      const collabIndex = state.collaboratorPayrolls.findIndex(
        (payroll) => payroll.id === action.payload.id
      );
      if (collabIndex !== -1) {
        state.collaboratorPayrolls[collabIndex] = action.payload;
      }

      // Update in period payrolls if exists
      const periodIndex = state.periodPayrolls.findIndex(
        (payroll) => payroll.id === action.payload.id
      );
      if (periodIndex !== -1) {
        state.periodPayrolls[periodIndex] = action.payload;
      }
    });

    builder.addCase(PayrollThunks.deleteItem.fulfilled, (state, action) => {
      state.payrolls = state.payrolls.filter(
        (payroll) => payroll.id !== action.payload
      );
      state.collaboratorPayrolls = state.collaboratorPayrolls.filter(
        (payroll) => payroll.id !== action.payload
      );
      state.periodPayrolls = state.periodPayrolls.filter(
        (payroll) => payroll.id !== action.payload
      );
    });

    // Bulk operations
    builder.addCase(PayrollThunks.bulkSave.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(PayrollThunks.bulkSave.fulfilled, (state, action) => {
      state.payrolls.push(...action.payload);
      state.isLoading = false;
    });
    builder.addCase(PayrollThunks.bulkSave.rejected, (state) => {
      state.isLoading = false;
    });

    // Get payrolls by period
    builder.addCase(PayrollThunks.getPayrollsByPeriod.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      PayrollThunks.getPayrollsByPeriod.fulfilled,
      (state, action) => {
        state.periodPayrolls = action.payload;
        state.isLoading = false;
      }
    );
    builder.addCase(PayrollThunks.getPayrollsByPeriod.rejected, (state) => {
      state.isLoading = false;
    });

    // Get payrolls by collaborator
    builder.addCase(
      PayrollThunks.getPayrollsByCollaborator.pending,
      (state) => {
        state.isLoading = true;
      }
    );
    builder.addCase(
      PayrollThunks.getPayrollsByCollaborator.fulfilled,
      (state, action) => {
        state.collaboratorPayrolls = action.payload;
        state.isLoading = false;
      }
    );
    builder.addCase(
      PayrollThunks.getPayrollsByCollaborator.rejected,
      (state) => {
        state.isLoading = false;
      }
    );

    // Approve payroll
    builder.addCase(PayrollThunks.approvePayroll.fulfilled, (state, action) => {
      const index = state.payrolls.findIndex(
        (payroll) => payroll.id === action.payload.id
      );
      if (index !== -1) {
        state.payrolls[index] = action.payload;
      }

      // Update in other arrays too
      const collabIndex = state.collaboratorPayrolls.findIndex(
        (payroll) => payroll.id === action.payload.id
      );
      if (collabIndex !== -1) {
        state.collaboratorPayrolls[collabIndex] = action.payload;
      }

      const periodIndex = state.periodPayrolls.findIndex(
        (payroll) => payroll.id === action.payload.id
      );
      if (periodIndex !== -1) {
        state.periodPayrolls[periodIndex] = action.payload;
      }
    });

    // Process payment
    builder.addCase(PayrollThunks.processPayment.fulfilled, (state, action) => {
      const index = state.payrolls.findIndex(
        (payroll) => payroll.id === action.payload.id
      );
      if (index !== -1) {
        state.payrolls[index] = action.payload;
      }

      // Update in other arrays too
      const collabIndex = state.collaboratorPayrolls.findIndex(
        (payroll) => payroll.id === action.payload.id
      );
      if (collabIndex !== -1) {
        state.collaboratorPayrolls[collabIndex] = action.payload;
      }

      const periodIndex = state.periodPayrolls.findIndex(
        (payroll) => payroll.id === action.payload.id
      );
      if (periodIndex !== -1) {
        state.periodPayrolls[periodIndex] = action.payload;
      }
    });
  },
});

export const { clearCollaboratorPayrolls, clearPeriodPayrolls } =
  payrollSlice.actions;
export default payrollSlice.reducer;
